import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

export const FadeUp = ({ children, delay = 0, className = "", y = 30 }) => (
    <motion.div
        className={className}
        initial={{ opacity: 0, y }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-70px" }}
        transition={{ duration: 0.75, delay, ease: EASE }}
    >
        {children}
    </motion.div>
);

export const MaskedLine = ({ children, delay = 0, className = "" }) => (
    <span className={`block overflow-hidden ${className}`}>
        <motion.span
            className="block"
            initial={{ y: "112%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.95, delay, ease: EASE }}
        >
            {children}
        </motion.span>
    </span>
);

export const Counter = ({ to, suffix = "", decimals = 0, className = "" }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-40px" });
    const [val, setVal] = useState(0);

    useEffect(() => {
        if (!inView) return undefined;
        const controls = animate(0, to, {
            duration: 1.9,
            ease: EASE,
            onUpdate: (v) => setVal(v),
        });
        return () => controls.stop();
    }, [inView, to]);

    return (
        <span ref={ref} className={className}>
            {val.toFixed(decimals)}
            {suffix}
        </span>
    );
};

export const Eyebrow = ({ children }) => (
    <span className="inline-flex items-center gap-2 rounded-full border border-[#00F0FF]/30 bg-[#00F0FF]/5 px-4 py-1.5 font-mono text-[11px] font-semibold tracking-[0.28em] text-[#00F0FF]">
        {children}
    </span>
);

export const PageHeader = ({ eyebrow, title, subtitle, testId }) => (
    <section className="bg-grid relative overflow-hidden pb-14 pt-32 sm:pb-20 sm:pt-40">
        <div
            className="pointer-events-none absolute -top-32 left-1/2 h-96 w-[42rem] -translate-x-1/2 rounded-full bg-[#E61C24]/15 blur-[120px]"
            aria-hidden="true"
        />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-testid={testId}>
            <FadeUp>
                <Eyebrow>{eyebrow}</Eyebrow>
            </FadeUp>
            <FadeUp delay={0.1}>
                <h1 className="font-display mt-6 max-w-3xl text-4xl font-black leading-[1.02] tracking-tight text-white sm:text-5xl lg:text-6xl">
                    {title}
                </h1>
            </FadeUp>
            {subtitle && (
                <FadeUp delay={0.2}>
                    <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg">
                        {subtitle}
                    </p>
                </FadeUp>
            )}
        </div>
    </section>
);
